import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { EAN_MAX_LENGTH } from 'src/app/shared/eans.config';

export const INVALID_CLASS_NAME = 'is-invalid';

/**
 * A conditional validator generator. Assigns a validator to the form control if the predicate function returns true on the moment of validation
 * @example
 * ngOnInit() {
 *   this.myForm = this.fb.group({
 *    myEmailField: ['', [
 *       Validators.maxLength(250),
 *       Validators.minLength(5),
 *       conditionalValidator(
 *         () => this.myForm.get('myCheckbox').value,
 *         Validators.compose([
 *            Validators.required,
 *            Validators.pattern(/.*mason/)
 *         ]),
 *         'illuminatiError')
 *        ]]
 *     })
 * }
 * ToDo: figure out how to tell form to check this field validity when predicate is true
 * !IMPORTANT! Need to inform form when myCheckbox changes it value that enables validators
 * this.form.get('myCheckbox')?.valueChange
 *       .subscribe((value) => {
 *         this.form.get('myEmailField')?.updateValueAndValidity();
 *       });
 * @param predicate
 * @param validator
 * @param errorNamespace optional argument that creates own namespace for the validation error
 */
export function conditionalValidator(
  predicate: () => boolean,
  validator: ValidatorFn | null,
  errorNamespace?: string): ValidatorFn {
  return (formControl => {
    if (!formControl.parent) {
      return null;
    }

    let error = null;
    if (predicate() && validator) {
      error = validator(formControl);
    }

    if (errorNamespace && error) {
      const customError: Record<string, ValidationErrors> = {};
      customError[errorNamespace] = error;
      error = customError
    }
    return error;
  })
}

export const notOnlyWhitespacesValidator: ValidatorFn = formControl => {
  if (/^\s*$/i.test(String(formControl.value))) {
    return {
      notOnlyWhitespaces: 'This control cannot have white spaces only',
    };
  }

  return null;
}

export const hostValidator = (allowedHosts: string[], httpsOnly: boolean = false) => {
  const validator: ValidatorFn = formControl => {

    try {
      const { host, protocol } = new URL(formControl.value);

      if (!allowedHosts.includes(host)) {
        return {
          hostValidatorNotAllowed: 'Unknown host for URL'
        };
      }

      if (httpsOnly && !protocol.startsWith('https')) {
        return {
          hostValidatorIncorrectProtocol: 'Only HTTPS protocol is allowed',
        };
      }
    } catch (e) {
      return {
        hostValidatorIncorrectURL: 'This URL is not valid',
      };
    }

    return null;
  };

  return validator;
};

export const urlValidator = (baseUrls: string[] = [], allowEmpty = false): ValidatorFn => formControl => {
  if (allowEmpty && !formControl.value) {
    return null;
  }

  try {
    // Trim spaces before validating
    const trimmedValue = formControl.value?.trim();
    new URL(trimmedValue);

    if (!baseUrls.length) {
      return null;
    }

    const url = String(trimmedValue);
    const hasValidBaseUrl = baseUrls.find(baseUrl => url.startsWith(baseUrl));
    if (!hasValidBaseUrl) {
      return {
        urlValidator: 'This URL does not contain valid base URL',
      }
    }
  } catch (e) {
    return {
      urlValidator: 'This URL is not valid',
    };
  }

  return null;
};

export const arrayValidator = (itemValidator: ValidatorFn): ValidatorFn => formControl => {
  if (!Array.isArray(formControl.value)) {
    return null;
  }

  const invalidElements = formControl.value
    .map(itemValue => itemValidator(new FormControl(itemValue)))
    .filter<ValidationErrors>((validationResult): validationResult is ValidationErrors => !!validationResult);

  return invalidElements.length ? invalidElements[0] : null;
}

/**
 * @todo Remove or merge with urlValidator cuz seem like they both are validate the same
 */
export const url2Validator: ValidatorFn = formControl => {
  const regExp = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
  if (!regExp.test(formControl.value)) {
    return {
      url2Validator: 'This URL should contain http:// or https:// at the beggining'
    }
  }

  return null;
}

export const equalValidator: (_: any) => ValidatorFn = (value: any) => formControl => {
  if (formControl.value != value) {
    return {
      equal: `This value should equal ${value}`,
    };
  }

  return null;
};

export const matchPassword = (fieldOne: string, fieldTwo: string): ValidatorFn =>  (control: AbstractControl) => {
  const value1 = control.get(fieldOne)?.value;
  const value2 = control.get(fieldTwo)?.value;

  if (value1 && value2 && value1 !== value2) {
    return {
      matchPassword: 'Passwords should match'
    }
  }

  return null;
}

export const eanValidator: ValidatorFn = formControl => {
  const value = formControl.value;
  const errorMessage: { 'invalidEans': string } = { 'invalidEans': 'Invalid EANs' };

  if (!value) {
    return null;
  }

  const eans = value.split(/[,\s]+/);

  for (const ean of eans) {
    if (ean.length > EAN_MAX_LENGTH) {
      return { 'maxLengthExceeded': true };
    }
  }

  return /^(\d+\s*,?\s*)+$/.test(value) ? null : errorMessage;
}

export const atLeastOne = (validator: ValidatorFn, controls: string[]) => (
  controller: AbstractControl,
): ValidationErrors | null => {
  const group = controller as FormGroup;

  if (!controls){
    controls = Object.keys(group.controls)
  }

  const hasAtLeastOne = group && group.controls && controls
    .some(k => !validator(group.controls[k]));

  return hasAtLeastOne ? null : {
    atLeastOne: true,
  };
}

export function numberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  /**
   * @todo: Remove check of pristine property
   *  Validator shouldn't check if control is pristine, it's responsibility of component
   */
  if (value === null || value === '' || control.pristine) return null;
  return !isNaN(value) && Number.isInteger(Number(value)) ? null : { notANumber: true };
}

export const scrollToErrorMessage = (): void => {
  document.getElementsByClassName('invalid-feedback')[0]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
